/**
 * This service provides an interface for loading and saving communication preferences (aka "e-delivery") for
 * a participant. It also wraps our API calls with promises for convenience.
 */
import { AMPLITUDE_EVENTS } from "../amplitude";
import ExternalLogger from "../logger/ExternalLogger";

import { updateParticipantConsent, retrieveParticipantConsent } from "./EDeliveryFactory";
import eDeliveryEvents from "./event/EDeliveryEvents";

const EDeliveryService = () => {
    const logger = ExternalLogger.getInstance("EDeliveryService");
    /**
     * Initialize the e-delivery service by fetching the eConsent data for the participant and storing it
     * in the factory. Return whether or not the participant is allowed to change their communication preference.
     *
     * This is used so the parent view can determine whether or not the communication preferences component
     * should display.
     *
     * @param  {String}  gaId The participant's group ID
     * @return {Promise}      A promise that the participant's communication preferences will be initialized
     */
    const initialize = async (gaId) => {
        const res = await retrieveParticipantConsent(gaId);
        if (!res || !res.data) {
            logger.error(
                "No response for participant's communication preferences for group id {0}",
                gaId
            );
        }
        return res.data;
    };

    /**
     * Update the participant's communication preference.
     *
     * @param  {String}  flowName The name of the current flow
     * @return {Promise}          A promise that the participant's communication preference will be updated in the db
     */
    const updateParticipantEConsent = async (flowName, gaId, email, consentInd, eventBus) => {
        const params = {
            email: email || "",
            consentInd: consentInd,
            gaId: gaId,
            flowName: flowName
        };

        eventBus.dispatch(eDeliveryEvents.E_CONSENT_SUBMIT, this, {
            values: params
        });
        eventBus.dispatchAmplitude({
            event_type: AMPLITUDE_EVENTS.SELECT_BUTTON,
            event_properties: {
                selection: eDeliveryEvents.E_CONSENT_SUBMIT,
                values: params
            }
        });

        const res = await updateParticipantConsent(params);

        if (res.status === 200) {
            logger.debug("Successfully updated participant's communication preferences");
        }

        if (res.status !== 200 || !res) {
            eventBus.dispatch(eDeliveryEvents.E_CONSENT_SUBMIT_INVALID, this);
            eventBus.dispatchAmplitude({
                event_type: AMPLITUDE_EVENTS.VIEW_PAGE,
                event_properties: {
                    selection: eDeliveryEvents.E_CONSENT_SUBMIT_INVALID
                }
            });
            logger.error(
                "Failed to update participant's communication preferences. Parameters: {0}. Error {1}",
                [JSON.stringify(params), JSON.stringify(res.error)]
            );
        }
        return res;
    };

    return {
        initialize: initialize,
        updateParticipantEConsent: updateParticipantEConsent
    };
};
export default EDeliveryService;
